/**
 * Serialized topic (Hasura-friendly). Monthly pageviews used for ad pricing.
 * @typedef {{
 *   views?: number | string | null,
 *   views_per_month?: number | string | null,
 *   viewsPerMonth?: number | string | null,
 *   name?: string | null,
 * }} SerializedTopicLike
 */

function parseViewsRaw(raw) {
  if (raw == null || raw === "") {
    return 0
  }
  if (typeof raw === "number") {
    return Number.isFinite(raw) && raw >= 0 ? Math.floor(raw) : 0
  }
  const s = String(raw)
    .replace(/,/g, "")
    .trim()
  const n = Number(s)
  if (!Number.isFinite(n) || n < 0) {
    return 0
  }
  return Math.floor(n)
}

function readMonthlyViews(topic) {
  if (topic == null || typeof topic !== "object") {
    return 0
  }
  const raw =
    topic.views_per_month != null
      ? topic.views_per_month
      : topic.viewsPerMonth != null
      ? topic.viewsPerMonth
      : topic.views != null
      ? topic.views
      : 0
  return parseViewsRaw(raw)
}

const VIEWS_PER_PACKAGE = 500
const USD_PER_PACKAGE = 50

/**
 * One-time checkout URLs by monthly USD tier (same URLs in all environments). When adding a tier,
 * add a key here and in {@link PAID_AD_TOPIC_SUBSCRIPTION_LINK_BY_PRICE_USD}; {@link PAID_AD_PRICE_LINK_MAX_USD}
 * follows the max key on this object.
 * @type {Record<number, string>}
 */
const PAID_AD_TOPIC_LINK_BY_PRICE_USD = {
  50: "https://buy.stripe.com/bJe3cw7vdd3KegubLwaMU0m",
  100: "https://buy.stripe.com/9B66oIeXF4xec8m5n8aMU0i",
  150: "https://buy.stripe.com/4gM6oI16P4xegoCaHsaMU0j",
  200: "https://buy.stripe.com/7sY6oIbLtfbSa0eaHsaMU0k",
  250: "https://buy.stripe.com/3cIbJ202L8Nua0e9DoaMU0l",
}

/** Subscription checkout URLs; tier keys must match one-time map. @type {Record<number, string>} */
const PAID_AD_TOPIC_SUBSCRIPTION_LINK_BY_PRICE_USD = {
  50: "https://buy.stripe.com/eVq5kE02L7Jq6O28zkaMU0h",
  100: "https://buy.stripe.com/00wbJ2cPxfbS4FUeXIaMU0n",
  150: "https://buy.stripe.com/28E5kEdTB0gY3BQeXIaMU0o",
  200: "https://buy.stripe.com/7sY9AUbLt8Nu5JY3f0aMU0p",
  250: "https://buy.stripe.com/dRmfZi02L4xegoC4j4aMU0q",
}

/** Max billed price for paid ads; highest tier with a checkout link. */
export const PAID_AD_PRICE_LINK_MAX_USD = Math.max(
  ...Object.keys(PAID_AD_TOPIC_LINK_BY_PRICE_USD).map(Number)
)

/**
 * Paid topic-sidebar ad: $50 per 500 monthly views (Stripe-style package pricing).
 * Billable view buckets use ceil(views / 500), minimum one bucket ($50). Examples: 0–500 → $50;
 * 501–1000 → $100; 1390 → $150 (3×500).
 * Capped at PAID_AD_PRICE_LINK_MAX_USD so the amount always matches a payment link tier.
 * @param {SerializedTopicLike | null | undefined} topic
 * @returns {number}
 */
export function getMonthlyPaidAdPriceForTopic(topic) {
  const views = readMonthlyViews(topic)
  const packages = Math.max(1, Math.ceil(views / VIEWS_PER_PACKAGE))
  const uncappedUsd = USD_PER_PACKAGE * packages
  return Math.min(uncappedUsd, PAID_AD_PRICE_LINK_MAX_USD)
}

/**
 * One-time checkout URL for this topic’s traffic tier.
 * @param {SerializedTopicLike | null | undefined} topic
 * @returns {string}
 */
export function getLinkForTopicAd(topic) {
  const priceUsd = getMonthlyPaidAdPriceForTopic(topic)
  return PAID_AD_TOPIC_LINK_BY_PRICE_USD[priceUsd] ?? ""
}

/**
 * Subscription checkout URL for this topic’s traffic tier ({@link getLinkForTopicAd} is one-time).
 * @param {SerializedTopicLike | null | undefined} topic
 * @returns {string}
 */
export function getSubscriptionLinkForTopicAd(topic) {
  const priceUsd = getMonthlyPaidAdPriceForTopic(topic)
  return PAID_AD_TOPIC_SUBSCRIPTION_LINK_BY_PRICE_USD[priceUsd] ?? ""
}
